// cpq2-owned narrowing for a TABLE COLUMN's cell options.
//
// A type-11 question's dropdown cells take their options from the column's
// `custrecord_cpqc_answer_qc_opt` — a JSON array of strings on the ANSWER that represents the
// column. answerHideRules cannot reach them: its answers ARE the columns, so hiding one hides
// the whole column rather than a value inside it.
//
// Kevin, 2026-09-21, on NDB's Inlet Connection Size: "these are the only sizes that exist ...
// can you stop showing the ones that do not exist but may exist in the future?" Twenty-one
// sizes were offered; eight are real parts. The other thirteen are not wrong for ever, which
// is why this narrows a list rather than deleting anything.
//
// WHY NOT EDIT questions.json — the same reason answerHideRules exists. extract-product.cjs
// rewrites that folder wholesale on the next import, so the edit would vanish with nothing
// failing: the dropdown would simply go back to twenty-one sizes and nobody would know until
// someone ordered a 9/32" coupling.
import { getProfile } from "../products";
import type { Question } from "../types";

/** Parse a qc_opt payload. Tolerant: a malformed one yields no options rather than throwing. */
function parseOptions(raw: unknown): string[] | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.map((x) => String(x)) : null;
  } catch {
    return null;
  }
}

/**
 * Apply a product's tableCellOptions to its questions, IN PLACE.
 *
 * Returns how many columns were narrowed — what a caller logs and a test asserts on. A rule
 * naming a question, a column or an option that does not exist would otherwise do nothing,
 * silently, and look exactly like a rule that was applied.
 *
 * NARROWS ONLY. An option absent from the column's own list is never added, whatever the rule
 * says, so this can restrict what the export offers and never invent a value the encoder has
 * no token for. Order is the column's own, not the rule's.
 */
export function applyTableCellOptions(product: string, questions: Question[]): number {
  const rules = getProfile(product).tableCellOptions ?? [];
  if (rules.length === 0) return 0;
  let touched = 0;
  for (const rule of rules) {
    const q = questions.find((x) => x.custrecord_cpqc_question_code === rule.question);
    if (!q || !Array.isArray(q._answers)) continue;
    const keep = new Set(rule.keep);
    for (const col of q._answers) {
      const code = col.custrecord_cpqc_answer_code;
      if (rule.columns && !rule.columns.includes(code)) continue;
      const opts = parseOptions(col.custrecord_cpqc_answer_qc_opt);
      if (!opts) continue;
      // "None" is the absence of a connection, not a size, and every rule keeps it when the
      // column already offered it -- dropping it would make the column unanswerable.
      const next = opts.filter((o) => keep.has(o) || o === "None");
      if (next.length === opts.length) continue; // nothing to narrow
      if (next.length === 0) continue; // a rule that empties a column is a bug, not a rule
      col.custrecord_cpqc_answer_qc_opt = JSON.stringify(next);
      touched++;
    }
  }
  return touched;
}
